import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useMotionValue, useTransform } from "framer-motion";
import { Music, VolumeX } from "lucide-react";
import { StarfieldCanvas } from "../components/StarfieldCanvas";
import { startHarp, stopHarp } from "../audio/harp";
import { useLang } from "../store/lang";
import { DrawGate } from "./DrawGate";
import { SceneGarden, SceneInvite, SceneLanterns, SceneMoon, SceneName } from "./StoryScenes";

const PER = 100;
const TOTAL = PER * 5;
const ROMANS = ["I", "II", "III", "IV", "V"];

export const JourneyPage = () => {
  const [entered, setEntered] = useState(false);
  const [audioOn, setAudioOn] = useState(false);
  const { ur, toggle } = useLang();
  const progress = useMotionValue(0);
  const target = useRef(0);
  const rootRef = useRef(null);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
      stopHarp();
    };
  }, []);

  useEffect(() => {
    if (!entered) return;
    startHarp();
    setAudioOn(true);
    const el = rootRef.current;
    const clampT = () => {
      target.current = Math.min(TOTAL, Math.max(0, target.current));
    };
    const onWheel = (e) => {
      e.preventDefault();
      target.current += e.deltaY * 0.09;
      clampT();
    };
    let lastY = null;
    const onTS = (e) => {
      lastY = e.touches[0].clientY;
    };
    const onTM = (e) => {
      if (lastY === null) return;
      e.preventDefault();
      const y = e.touches[0].clientY;
      target.current += (lastY - y) * 0.32;
      lastY = y;
      clampT();
    };
    const onKey = (e) => {
      if (["ArrowDown", "PageDown", " "].includes(e.key)) {
        e.preventDefault();
        target.current += 45;
        clampT();
      }
      if (["ArrowUp", "PageUp"].includes(e.key)) {
        e.preventDefault();
        target.current -= 45;
        clampT();
      }
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    el.addEventListener("touchstart", onTS, { passive: true });
    el.addEventListener("touchmove", onTM, { passive: false });
    window.addEventListener("keydown", onKey);
    let raf;
    const loop = () => {
      progress.set(progress.get() + (target.current - progress.get()) * 0.085);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("touchstart", onTS);
      el.removeEventListener("touchmove", onTM);
      window.removeEventListener("keydown", onKey);
      cancelAnimationFrame(raf);
    };
  }, [entered, progress]);

  const counterText = useTransform(progress, (v) => String(Math.max(0, Math.round(99 - (v / TOTAL) * 99))).padStart(2, "0"));
  const chapter = useTransform(progress, (v) => ROMANS[Math.min(4, Math.max(0, Math.floor(v / PER)))]);
  const barScale = useTransform(progress, [0, TOTAL], [0, 1]);

  return (
    <div
      ref={rootRef}
      data-testid="journey-page"
      className="fixed inset-0 bg-gradient-to-b from-[#070814] via-[#0d0f22] to-[#19102e] overflow-hidden"
      style={{ touchAction: "none" }}
    >
      <StarfieldCanvas className="absolute inset-0 w-full h-full" />

      {!entered && (
        <button
          data-testid="journey-lang-toggle"
          onClick={toggle}
          aria-label="Toggle Urdu"
          className="absolute top-4 right-5 sm:right-8 z-50 text-[0.72rem] px-3.5 py-1.5 rounded-full border border-amber-200/25 text-amber-200 hover:border-amber-300/60 transition-colors duration-300 bg-[#0B0D1B]/60 backdrop-blur"
        >
          {ur ? "EN" : "اردو"}
        </button>
      )}

      <SceneMoon progress={progress} />
      <SceneGarden progress={progress} />
      <SceneName progress={progress} />
      <SceneLanterns progress={progress} />
      <SceneInvite progress={progress} />

      {entered && (
        <>
          <header className="absolute top-0 inset-x-0 z-30 flex items-center justify-between px-5 sm:px-8 py-4">
            <a href="/" data-testid="journey-home-link" className="text-amber-200 font-royal tracking-[0.25em] text-sm">
              H · N · J
            </a>
            <span className="text-[0.6rem] tracking-[0.35em] uppercase text-amber-100/60">
              {ur ? "باب" : "chapter"} <motion.span data-testid="journey-chapter">{chapter}</motion.span>
            </span>
            <span className="flex items-center gap-3">
              <span className="text-[0.6rem] tracking-[0.35em] uppercase text-amber-100/60">
                {ur ? "چاندنی" : "moonlight"}{" "}
                <motion.span data-testid="journey-counter" className="text-amber-200">
                  {counterText}
                </motion.span>
              </span>
              <button
                data-testid="journey-audio-toggle"
                onClick={() => {
                  if (audioOn) {
                    stopHarp();
                    setAudioOn(false);
                  } else {
                    startHarp();
                    setAudioOn(true);
                  }
                }}
                aria-label="Toggle harp melody"
                className="w-9 h-9 rounded-full glass-card flex items-center justify-center text-amber-200"
              >
                {audioOn ? <Music size={15} /> : <VolumeX size={15} />}
              </button>
              <button
                data-testid="journey-lang-toggle-hud"
                onClick={toggle}
                aria-label="Toggle Urdu"
                className="text-[0.72rem] px-3 py-1.5 rounded-full border border-amber-200/25 text-amber-200 hover:border-amber-300/60 transition-colors duration-300"
              >
                {ur ? "EN" : "اردو"}
              </button>
            </span>
          </header>
          <div className="absolute bottom-0 inset-x-0 h-[3px] bg-white/5 z-30">
            <motion.div
              data-testid="journey-progress-bar"
              className="h-full origin-left bg-gradient-to-r from-amber-300 to-rose-200"
              style={{ scaleX: barScale }}
            />
          </div>
        </>
      )}

      <AnimatePresence>{!entered && <DrawGate onEnter={() => setEntered(true)} />}</AnimatePresence>
    </div>
  );
};
